import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { CalendarMonthGrid, CalendarRail, CalendarTimeGrid, CalendarToolbar, EventQuickView, HiddenTagsContext, RescheduleConfirmModal } from '../components/calendar';
import { Skeleton } from '../components/ui/skeleton';
import { buildTagFilters, useCalendarEvents, useCalendarStatus } from '../hooks/useCalendar';
import { useOutlookCalendarStatus } from '../hooks/useOutlook';
import { SCHEDULE_DISABLED_COPY, useScheduleCapabilities } from '../hooks/useScheduleCapabilities';
import { useAuth } from '../context/AuthContext';
import { resolveDisplayZone } from '../lib/timezone';
import { errorText } from '../lib/apiErrors';
import { readPref, writePref } from '../lib/localPrefs';
import {
    EMPTY_HIDDEN, isAnythingHidden, loadHiddenTags, pruneHidden, saveHiddenTags, setTagVisible, setUntaggedVisible,
} from '../lib/calendar/tagFilter';
import {
    eventDayKey, instantAt, isDayKey, minutesOf, rangeFor, stepAnchor, todayKey as zoneToday, weekDaysOf,
} from '../lib/calendar/zonedCalendar';

// Agenda was removed by #679 (the design has three views, and restyling the
// shared chip would have left it half-converted). A viewer who last used it
// still has 'agenda' stored — it, and any other unknown value, falls back to
// Week, which is also the default.
export const VIEW_MODES = ['day', 'week', 'month'];
export const VIEW_MODE_STORAGE_KEY = 'nurturecrm.calendar.viewMode';
const DEFAULT_VIEW = 'week';

export function loadPersistedViewMode() {
    return readPref(VIEW_MODE_STORAGE_KEY, DEFAULT_VIEW, (v) => VIEW_MODES.includes(v));
}

/** Start/end for a new event: the next half hour today, else 9:00 on the anchor day. */
function defaultNewEventWindow(anchorDay, today, zone) {
    let minutes = 9 * 60;
    if (anchorDay === today) {
        const now = minutesOf(new Date(), zone);
        minutes = Math.min(Math.ceil((now + 1) / 30) * 30, 23 * 60 + 30);
    }
    const start = instantAt(anchorDay, minutes, zone);
    return { start, end: new Date(start.getTime() + 30 * 60 * 1000) };
}

export function newEventHref(start, end) {
    const q = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
    return `/calendar/new?${q.toString()}`;
}

/**
 * The calendar page (#677): toolbar, rail and grid in a bounded fullscreen
 * column (on RuntimeShell's isFullscreenPage list). The grid, the rail and the
 * editor surfaces own their own scroll regions.
 *
 * Everything is placed and labelled in ONE display zone (profile → browser),
 * resolved here once and threaded down — #540's rule, now extended from the
 * labels to the placement (fold-in Q4b). Navigation state is a day key in that
 * zone, never a browser-local Date.
 */
export default function CalendarPage() {
    const { user } = useAuth();
    const displayZone = resolveDisplayZone({ user });
    const today = zoneToday(displayZone);
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [view, setViewState] = useState(loadPersistedViewMode);
    const [anchorDay, setAnchorDay] = useState(() => (isDayKey(location.state?.anchorDay) ? location.state.anchorDay : today));
    const [selectedEvent, setSelectedEvent] = useState(() => location.state?.openEvent || null);
    const [rescheduleDraft, setRescheduleDraft] = useState(null);
    const [railOpen, setRailOpen] = useState(false);

    const setView = useCallback((next) => {
        setViewState(next);
        writePref(VIEW_MODE_STORAGE_KEY, next);
    }, []);

    // #681 My calendars: the hidden set is page state, per user, persisted like
    // the view mode. It feeds the events query (key + params), independent of
    // the view and the range, so switching view or paging keeps it applied.
    const userId = user?.user_id;
    const [hidden, setHiddenState] = useState(() => loadHiddenTags(userId));
    useEffect(() => { setHiddenState(loadHiddenTags(userId)); }, [userId]);
    const setHidden = useCallback((next) => {
        setHiddenState(next);
        saveHiddenTags(userId, next);
    }, [userId]);
    const tagFilters = useMemo(() => buildTagFilters(hidden), [hidden]);
    const hiddenTagIds = useMemo(() => new Set(hidden.tagIds), [hidden.tagIds]);

    // Returning from the editor or the full event view: take the day and the
    // quick view it asked for, then clear the router state so a reload does
    // not reopen it.
    useEffect(() => {
        if (location.state?.anchorDay || location.state?.openEvent) {
            navigate(location.pathname + location.search, { replace: true, state: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { fromIso, toIso } = useMemo(() => rangeFor(view, anchorDay, displayZone), [view, anchorDay, displayZone]);
    const {
        data: eventsData, isLoading, isError, error, refetch, isFetching, isPlaceholderData,
    } = useCalendarEvents(fromIso, toIso, { filters: tagFilters });
    const events = eventsData?.events || [];
    // Every event in range is hidden by My calendars — say so, never show a
    // silently empty grid (or a silently full one).
    const allHidden = Boolean(eventsData) && !isPlaceholderData && isAnythingHidden(hidden)
        && events.length === 0 && (eventsData.totalUnfiltered || 0) > 0;

    const myCalendars = useMemo(() => ({
        ready: Boolean(eventsData),
        facets: eventsData?.tagFacets || [],
        untaggedCount: eventsData?.untaggedCount || 0,
        degraded: Boolean(eventsData?.tagsDegraded),
        hidden,
        onToggleTag: (tagId, visible) => setHidden(setTagVisible(hidden, tagId, visible)),
        onToggleUntagged: (visible) => setHidden(setUntaggedVisible(hidden, visible)),
        onShowAll: () => setHidden(EMPTY_HIDDEN),
        onPrune: (known) => setHidden(pruneHidden(hidden, known)),
    }), [eventsData, hidden, setHidden]);

    // Connection state from the dedicated status endpoints, so it survives a
    // failed aggregated fetch.
    const { data: calendarStatus, isLoading: googleStatusLoading } = useCalendarStatus();
    const { data: outlookStatus } = useOutlookCalendarStatus();
    const { anyCalendarConnected } = useScheduleCapabilities();
    const googleConnected = calendarStatus?.google?.connected ?? (eventsData?.providers?.google === 'connected');
    const outlookConnected = Boolean(outlookStatus?.connected);
    const statusResolved = !googleStatusLoading && outlookStatus !== undefined;
    // D8: the banner is for "no calendar at all" — never for an Outlook-only
    // user, which the old `!googleConnected` gate nagged forever.
    const showNoCalendarBanner = statusResolved && !googleConnected && !outlookConnected;

    const openNewEvent = useCallback((start) => {
        if (!anyCalendarConnected) return;
        const s = start || defaultNewEventWindow(anchorDay, today, displayZone).start;
        navigate(newEventHref(s, new Date(s.getTime() + 30 * 60 * 1000)));
    }, [anyCalendarConnected, anchorDay, today, displayZone, navigate]);

    const goToDay = useCallback((dayKey) => { setAnchorDay(dayKey); setView('day'); }, [setView]);

    // Keyboard: D / W / M switch views, T jumps to today. Never while typing or
    // while a dialog is open.
    useEffect(() => {
        const onKey = (e) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const t = e.target;
            if (t && (t.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="alertdialog"], [role="menu"]'))) return;
            if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
            const key = e.key.toLowerCase();
            if (key === 'd') setView('day');
            else if (key === 'w') setView('week');
            else if (key === 'm') setView('month');
            else if (key === 't') setAnchorDay(zoneToday(displayZone));
            else return;
            e.preventDefault();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [setView, displayZone]);

    // #675: the Links tab is gone; its old URL still lands somewhere real.
    if (searchParams.get('tab') === 'links') return <Navigate to="/booking-links" replace />;

    const selectedDay = selectedEvent ? (eventDayKey(selectedEvent, displayZone) || anchorDay) : anchorDay;
    const createReason = anyCalendarConnected ? undefined : SCHEDULE_DISABLED_COPY;
    const dayKeys = view === 'day' ? [anchorDay] : weekDaysOf(anchorDay);

    let grid;
    if (isLoading && !eventsData) {
        grid = <GridLoading />;
    } else if (isError && !eventsData) {
        grid = <GridError message={errorText(error, 'The calendar service did not answer.')} onRetry={() => refetch()} retrying={isFetching} />;
    } else if (view === 'month') {
        grid = (
            <CalendarMonthGrid
                anchorDay={anchorDay}
                todayKey={today}
                displayZone={displayZone}
                events={events}
                onEventClick={setSelectedEvent}
                onDayClick={goToDay}
                onCellCreate={anyCalendarConnected ? openNewEvent : undefined}
                createDisabledReason={createReason}
                onRescheduleDraft={setRescheduleDraft}
            />
        );
    } else {
        grid = (
            <CalendarTimeGrid
                dayKeys={dayKeys}
                events={events}
                displayZone={displayZone}
                todayKey={today}
                onEventClick={setSelectedEvent}
                onSlotClick={anyCalendarConnected ? openNewEvent : undefined}
                slotDisabledReason={createReason}
                onDayClick={view === 'week' ? goToDay : undefined}
                onRescheduleDraft={setRescheduleDraft}
            />
        );
    }

    return (
        <HiddenTagsContext.Provider value={hiddenTagIds}>
        <div className="flex flex-col h-full min-h-0 bg-white" data-testid="calendar-page">
            <CalendarToolbar
                view={view}
                onViewChange={setView}
                anchorDay={anchorDay}
                displayZone={displayZone}
                onPrev={() => setAnchorDay(d => stepAnchor(view, d, -1))}
                onNext={() => setAnchorDay(d => stepAnchor(view, d, 1))}
                onToday={() => setAnchorDay(zoneToday(displayZone))}
                canCreate={anyCalendarConnected}
                createDisabledReason={createReason}
                onNewEvent={() => openNewEvent()}
                onToggleRail={() => setRailOpen(o => !o)}
                railOpen={railOpen}
            />
            {showNoCalendarBanner && (
                <div className="flex flex-none items-start gap-2.5 px-[18px] py-2.5 bg-[#FFFBEB] border-b border-[#FDE68A]" role="status" data-testid="calendar-no-provider-banner">
                    <AlertCircle className="w-4 h-4 mt-0.5 text-[#B45309] shrink-0" />
                    <p className="text-[12.5px] text-[#92400E]">
                        <span className="font-semibold">No calendar connected.</span>{' '}
                        Connect Google Calendar or Outlook to see and create meetings — bookings still appear here.{' '}
                        <Link to="/settings/integrations" className="font-medium underline">Go to Settings → Integrations</Link>
                    </p>
                </div>
            )}
            {isError && eventsData && (
                <div className="flex flex-none items-center gap-2 px-[18px] py-1.5 bg-[#FEF2F2] border-b border-[#F3D3D3] text-[12px] text-[#A3382B]" role="alert" data-testid="calendar-refresh-error">
                    Couldn't refresh — showing what was loaded before.
                    <button type="button" onClick={() => refetch()} className="font-medium underline">Retry</button>
                </div>
            )}
            <div className="flex flex-1 min-h-0 items-stretch">
                {/* In flow at every width (it pushes the grid, never overlays it);
                    below lg it is behind the toolbar toggle. */}
                <div className={`${railOpen ? 'flex' : 'hidden'} lg:flex flex-none min-h-0`}>
                    <CalendarRail
                        anchorDay={anchorDay}
                        todayKey={today}
                        view={view}
                        onPickDay={(key) => { setAnchorDay(key); setRailOpen(false); }}
                        googleConnected={Boolean(googleConnected)}
                        outlookStatus={outlookStatus}
                        statusLoading={googleStatusLoading}
                        myCalendars={myCalendars}
                    />
                </div>
                <div
                    className={`relative flex flex-1 min-w-0 min-h-0 flex-col transition-opacity ${isPlaceholderData && isFetching ? 'opacity-60' : ''}`}
                    aria-busy={isPlaceholderData && isFetching ? 'true' : undefined}
                >
                    {grid}
                    {allHidden && (
                        <AllHiddenNotice count={eventsData.totalUnfiltered} onShowAll={myCalendars.onShowAll} />
                    )}
                </div>
            </div>

            <EventQuickView
                event={selectedEvent}
                displayZone={displayZone}
                dayKey={selectedDay}
                viewerEmail={user?.email || ''}
                onClose={() => setSelectedEvent(null)}
            />
            <RescheduleConfirmModal
                draft={rescheduleDraft}
                displayZone={displayZone}
                onCancel={() => setRescheduleDraft(null)}
                onRescheduled={() => setRescheduleDraft(null)}
            />
        </div>
        </HiddenTagsContext.Provider>
    );
}

/** #681: the grid is empty because My calendars hides everything in range. */
function AllHiddenNotice({ count, onShowAll }) {
    return (
        <div className="absolute inset-0 flex items-center justify-center p-8 pointer-events-none">
            <div
                className="pointer-events-auto max-w-sm rounded-xl border border-[#E9E7E2] bg-white/95 px-5 py-4 text-center shadow-[0_8px_24px_rgba(12,10,9,.08)]"
                role="status"
                data-testid="calendar-all-hidden"
            >
                <p className="text-[13.5px] font-semibold text-[#0C0A09]">
                    {count === 1 ? 'The 1 event' : `All ${count} events`} in this range {count === 1 ? 'is' : 'are'} hidden by My calendars.
                </p>
                <p className="mt-1 text-[12px] text-[#8A837E]">Tick a calendar in the rail to see its events again.</p>
                <button
                    type="button"
                    onClick={onShowAll}
                    className="mt-3 inline-flex items-center h-8 px-3.5 rounded-[9px] border border-[#E4E1DB] bg-white text-[12.5px] font-medium text-[#3A3532] hover:bg-[#F4F2EE]"
                    data-testid="calendar-all-hidden-show-all"
                >
                    Show all
                </button>
            </div>
        </div>
    );
}

function GridLoading() {
    return (
        <div className="flex-1 min-h-0 p-4 flex flex-col gap-3" aria-busy="true" aria-label="Loading events" data-testid="calendar-loading">
            <div className="flex gap-3">
                {Array.from({ length: 7 }, (_, i) => <Skeleton key={i} className="h-9 flex-1 rounded-lg" />)}
            </div>
            <div className="flex-1 grid grid-cols-7 gap-3">
                {Array.from({ length: 7 }, (_, i) => (
                    <div key={i} className="flex flex-col gap-3 pt-6">
                        <Skeleton className="rounded-[7px]" style={{ height: 40 + ((i * 37) % 60) }} />
                        {i % 2 === 0 && <Skeleton className="h-14 rounded-[7px]" />}
                    </div>
                ))}
            </div>
        </div>
    );
}

function GridError({ message, onRetry, retrying }) {
    return (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 p-8 text-center" role="alert" data-testid="calendar-error">
            <AlertCircle className="w-8 h-8 text-[#A3382B]" />
            <div>
                <p className="text-[14px] font-semibold text-[#0C0A09]">Couldn't load your calendar.</p>
                <p className="text-[12.5px] text-[#8A837E] mt-1 max-w-md">{message} This is not the same as having no events.</p>
            </div>
            <button type="button" onClick={onRetry} disabled={retrying} className="inline-flex items-center gap-2 h-8 px-3.5 rounded-[9px] border border-[#E4E1DB] bg-white text-[12.5px] font-medium text-[#3A3532] hover:bg-[#F4F2EE]">
                <RefreshCw className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} /> Retry
            </button>
        </div>
    );
}
